'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { apiClient } from '@/lib/api-client';
import { shouldReflectSession, withSessionParam } from '@/lib/session-url';
interface UseSessionOptions {
  userId?: string | null;
}
function isSessionId(value: string | null | undefined): value is string {
  return !!value && value.startsWith('session_');
}
export function useSession(t0) {
  const $ = _c(33);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    userId
  } = options;
  const searchParams = useSearchParams();
  const storageKey = userId ? `crucible_last_session_${userId}` : "crucible_session_id";
  const needsServerSyncRef = useRef(false);
  let t2;
  if ($[2] !== searchParams) {
    t2 = () => {
      const urlSession = searchParams?.get("session");
      if (isSessionId(urlSession)) {
        return urlSession;
      }
      return "";
    };
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [sessionId, setSessionId] = useState(t2);
  const bootstrappedRef = useRef(false);
  let t3;
  let t4;
  if ($[4] !== searchParams || $[5] !== sessionId || $[6] !== storageKey || $[7] !== userId) {
    t3 = () => {
      if (bootstrappedRef.current) {
        return;
      }
      if (isSessionId(searchParams?.get("session"))) {
        bootstrappedRef.current = true;
        const urlSession_0 = searchParams.get("session");
        try {
          localStorage.setItem(storageKey, urlSession_0);
          localStorage.setItem("crucible_session_id", urlSession_0);
        } catch {}
        return;
      }
      if (sessionId) {
        bootstrappedRef.current = true;
        return;
      }
      bootstrappedRef.current = true;
      try {
        if (userId) {
          const scoped = localStorage.getItem(`crucible_last_session_${userId}`);
          if (scoped) {
            setSessionId(scoped);
            return;
          }
        } else {
          const legacy = localStorage.getItem("crucible_session_id");
          if (legacy) {
            setSessionId(legacy);
            return;
          }
        }
      } catch {}
      needsServerSyncRef.current = true;
      setSessionId(`session_${crypto.randomUUID()}`);
    };
    t4 = [searchParams, sessionId, storageKey, userId];
    $[4] = searchParams;
    $[5] = sessionId;
    $[6] = storageKey;
    $[7] = userId;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  const resolvedUserIdRef = useRef(userId);
  let t5;
  let t6;
  if ($[10] !== searchParams || $[11] !== userId) {
    t5 = () => {
      if (userId === resolvedUserIdRef.current) {
        return;
      }
      resolvedUserIdRef.current = userId;
      if (!userId) {
        return;
      }
      const urlSession_1 = searchParams?.get("session");
      if (isSessionId(urlSession_1)) {
        return;
      }
      const scopedKey = `crucible_last_session_${userId}`;
      const stored = localStorage.getItem(scopedKey);
      if (stored) {
        console.log("Re-resolved session for user from scoped key:", stored);
        setSessionId(stored);
        return;
      }
      needsServerSyncRef.current = true;
      const newId = `session_${crypto.randomUUID()}`;
      console.log("No scoped session for user; generated new session pending server sync:", newId);
      setSessionId(newId);
    };
    t6 = [userId, searchParams];
    $[10] = searchParams;
    $[11] = userId;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[14] !== userId) {
    t7 = () => {
      if (!needsServerSyncRef.current || !userId) {
        return;
      }
      needsServerSyncRef.current = false;
      apiClient.listConversations(userId).then(data => {
        const sessions = data.sessions ?? [];
        if (sessions.length > 0) {
          const mostRecent = sessions.sort(_temp)[0];
          if (mostRecent.session_id) {
            console.log("Synced to most recent server session:", mostRecent.session_id);
            setSessionId(mostRecent.session_id);
          }
        }
      }).catch(_temp2);
    };
    t8 = [userId];
    $[14] = userId;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  const deliberateSessionRef = useRef(null);
  const appliedUrlSessionRef = useRef(null);
  let t10;
  let t9;
  if ($[17] !== searchParams || $[18] !== sessionId) {
    t9 = () => {
      const urlSession_2 = searchParams?.get("session");
      if (isSessionId(urlSession_2) && urlSession_2 !== sessionId && appliedUrlSessionRef.current !== urlSession_2 && deliberateSessionRef.current !== sessionId) {
        appliedUrlSessionRef.current = urlSession_2;
        setSessionId(urlSession_2);
        console.log("Applied session ID from URL:", urlSession_2);
      }
    };
    t10 = [searchParams, sessionId];
    $[17] = searchParams;
    $[18] = sessionId;
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  const pathname = usePathname();
  let t11;
  let t12;
  if ($[21] !== pathname || $[22] !== sessionId) {
    t11 = () => {
      if (typeof window === "undefined" || !sessionId) {
        return;
      }
      if (!shouldReflectSession(pathname)) {
        return;
      }
      const next = withSessionParam(window.location.href, sessionId);
      if (next) {
        window.history.replaceState(window.history.state, "", next);
      }
    };
    t12 = [sessionId, pathname];
    $[21] = pathname;
    $[22] = sessionId;
    $[23] = t11;
    $[24] = t12;
  } else {
    t11 = $[23];
    t12 = $[24];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[25] !== sessionId || $[26] !== storageKey) {
    t13 = () => {
      if (typeof window !== "undefined" && sessionId) {
        localStorage.setItem(storageKey, sessionId);
        localStorage.setItem("crucible_session_id", sessionId);
        console.log("Saved session ID to localStorage:", sessionId);
      }
    };
    t14 = [sessionId, storageKey];
    $[25] = sessionId;
    $[26] = storageKey;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  useEffect(t13, t14);
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const newId_0 = `session_${crypto.randomUUID()}`;
      deliberateSessionRef.current = newId_0;
      setSessionId(newId_0);
      console.log("Reset to new session ID:", newId_0);
      return newId_0;
    };
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const resetSession = t15;
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = targetSessionId => {
      console.log("Switching to session:", targetSessionId);
      deliberateSessionRef.current = targetSessionId;
      setSessionId(targetSessionId);
    };
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const switchSession = t16;
  let t17;
  if ($[31] !== sessionId) {
    t17 = {
      sessionId,
      setSessionId,
      resetSession,
      switchSession
    };
    $[31] = sessionId;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
}

// UUID-based ID generators to prevent collisions with database IDs
function _temp2(err) {
  console.warn("[Session] Failed to sync from server:", err);
}
function _temp(a, b) {
  return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
}
export function useIdGenerator() {
  const $ = _c(1);
  const generateUniqueId = _temp3;
  const generateTabId = _temp4;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      generateUniqueId,
      generateTabId
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp4(type) {
  return `${type}_tab_${crypto.randomUUID()}`;
}
function _temp3(t0) {
  const prefix = t0 === undefined ? "local" : t0;
  return `${prefix}_${crypto.randomUUID()}`;
}